"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.onLCP = exports.LCPThresholds = void 0;

var _types = require("../../types.js");

var _bindReporter = require("./lib/bindReporter.js");

var _getActivationStart = require("./lib/getActivationStart.js");

var _getVisibilityWatcher = require("./lib/getVisibilityWatcher.js");

var _initMetric = require("./lib/initMetric.js");

var _observe = require("./lib/observe.js");

var _onHidden = require("./lib/onHidden.js");

var _runOnce = require("./lib/runOnce.js");

var _whenActivated = require("./lib/whenActivated.js");

var _whenIdle = require("./lib/whenIdle.js");

/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** Thresholds for LCP. See https://web.dev/articles/lcp#what_is_a_good_lcp_score */
const LCPThresholds = exports.LCPThresholds = [2500, 4000];
const reportedMetricIDs = {};
/**
 * Calculates the [LCP](https://web.dev/articles/lcp) value for the current page and
 * calls the `callback` function once the value is ready (along with the
 * relevant `largest-contentful-paint` performance entry used to determine the
 * value). The reported value is a `DOMHighResTimeStamp`.
 *
 * If the `reportAllChanges` configuration option is set to `true`, the
 * `callback` function will be called any time a new `largest-contentful-paint`
 * performance entry is dispatched, or once the final value of the metric has
 * been determined.
 */

const onLCP = (onReport, opts = {}) => {
  (0, _whenActivated.whenActivated)(() => {
    const visibilityWatcher = (0, _getVisibilityWatcher.getVisibilityWatcher)();
    const metric = (0, _initMetric.initMetric)('LCP');
    let report;

    const handleEntries = entries => {
      // If reportAllChanges is set then call this function for each entry,
      // otherwise only consider the last one.
      if (!opts.reportAllChanges) {
        // eslint-disable-next-line no-param-reassign
        entries = entries.slice(-1);
      }

      entries.forEach(entry => {
        // Only report if the page wasn't hidden prior to LCP.
        if (entry.startTime < visibilityWatcher.firstHiddenTime) {
          // The startTime attribute returns the value of the renderTime if it is
          // not 0, and the value of the loadTime otherwise. The activationStart
          // reference is used because LCP should be relative to page activation
          // rather than navigation start if the page was pre-rendered. But in cases
          // where `activationStart` occurs after the LCP, this time should be
          // clamped at 0.
          metric.value = Math.max(entry.startTime - (0, _getActivationStart.getActivationStart)(), 0);
          metric.entries = [entry];
          report();
        }
      });
    };

    const po = (0, _observe.observe)('largest-contentful-paint', handleEntries);

    if (po) {
      report = (0, _bindReporter.bindReporter)(onReport, metric, LCPThresholds, opts.reportAllChanges);
      const stopListening = (0, _runOnce.runOnce)(() => {
        if (!reportedMetricIDs[metric.id]) {
          handleEntries(po.takeRecords());
          po.disconnect();
          reportedMetricIDs[metric.id] = true;
          report(true);
        }
      }); // Stop listening after input. Note: while scrolling is an input that
      // stops LCP observation, it's unreliable since it can be programmatically
      // generated. See: https://github.com/GoogleChrome/web-vitals/issues/75

      ['keydown', 'click'].forEach(type => {
        // Wrap in a setTimeout so the callback is run in a separate task
        // to avoid extending the keyboard/click handler to reduce INP impact
        // https://github.com/GoogleChrome/web-vitals/issues/383
        if (_types.WINDOW.document) {
          addEventListener(type, () => (0, _whenIdle.whenIdle)(stopListening), {
            once: true,
            capture: true
          });
        }
      });
      (0, _onHidden.onHidden)(stopListening);
    }
  });
};

exports.onLCP = onLCP;